//================================================================
//  Component: Additional Licenses Or Overages Form
//================================================================

//  Purpose: This contains the form for a additional licenses or overages

//  Properties:
//    - collectionid = {string, Firestore collection id}
//    - documentid = {string, Firestore document id}
//    - task = {object, Firestore document}

//  Example:
//    <AdditionalLicensesOrOveragesForm
//      collectionid={collectionid}
//      documentid={documentid}
//      task={task}
//    ></AdditionalLicensesOrOveragesForm>    

// ==============================================================

// Libraries
import { useContext, useState } from 'react';

//Contexts
import { SetToast } from '../../../../../Library/GlobalContexts';

// Components
import LiveField from '../../../../../Components/LiveField/LiveField';
import DynamicVendorRating from '../../../../../Components/DynamicVendorRating/DynamicVendorRating';
import Tooltip from '../../../../../Components/Tooltip/Tooltip';
import KebabDropdown from '../../../../../Components/KebabDropdown/KebabDropdown';
import DynamicAddLinkLive from '../../../../../Components/DynamicAddLinkLive/DynamicAddLinkLive';
import FileUploadLiveField from '../../../../../Components/FileUploadLiveField/FileUploadLiveField';
import AddAllocationItemPane from '../SidePanes/AddAllocationItemPane';
import EditAllocationItemPane from '../SidePanes/EditAllocationItemPane';

//Functions
import WriteDocument from '../../../../../Library/WriteDocument';

// Images
import Add from '../../../../../Components/Images/Icon_Add_Teal.svg';
import AddDisabled from '../../../../../Components/Images/Icon_Add_Grey.svg';

// CSS

export default function AdditionalLicensesOrOveragesForm({
    collectionid,
    documentid,
    task
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to toggle the visibility of the add allocation item pane
    const [addAllocationItemPaneOpen, setAddAllocationItemPaneOpen] = useState(false);

    // Used to toggle the visibility of the edit allocation item pane
    const [editAllocationItemPaneOpen, setEditAllocationItemPaneOpen] = useState(false);

    // Used to store the index of the allocation item to be edited
    const [selectedAllocationIndex, setSelectedAllocationIndex] = useState(null)

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function to handle the edition of a cost allocation
    const handleAllocationItemEditClick = (allocationIndex) => {

        // Set the Allocation Index 
        setSelectedAllocationIndex(allocationIndex);

        // Open the Edit allocation item pane
        setEditAllocationItemPaneOpen(true);

    };

    // Function to handle the deletion of a cost allocation
    const handleAllocationItemDeleteClick = (allocationIndex) => {

        if (!task?.demandassessment?.allocations) {
            setToast({
                type: 'error',
                message: 'No allocations available to delete.',
            });
            return;
        }

        const updatedAllocations = task.demandassessment.allocations.filter((_, index) => index !== allocationIndex);

        // Prepare the updated document
        const updatedDoc = {
            'demandassessment': {
                ...task.demandassessment,
                'allocations': updatedAllocations
            }
        };

        WriteDocument('requests', task?.requestid, updatedDoc, true)
        .then(() => {
            setToast({
                type: 'success',
                message: 'Cost allocation deleted successfully!',
            });
        })
        .catch((error) => {
            console.error('Error deleting PO Line Item:', error);
            setToast({
                type: 'error',
                message: 'Failed to delete cost allocation Item.',
            });
        });

    };

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div>

            {/* ------------- Additional Licenses Or Overages------------- */}

            <h5 className='text-[18px] text-teal-600 mb-2'> Additional Licenses Or Overages</h5>

            {/*  1. Vendor Risk Rating (UpGuard)*/}
            <LiveField
                type={'lookup'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.vendor'}
                label={<label className='font-medium'> 1. Vendor Risk Rating (UpGuard)</label>}
                defaultValue={task?.demandassessment?.vendor?.vendorname}
                placeholder={'Search vendor by name'}
                dataFieldCollectionid={'vendors'}
                dataFieldPrimary={'vendorname'}
                dataFieldSecondary={'tradingas'}
                dataFieldTertiary={'overallrating'}
                dataFieldTertiaryrequired={false}
                dataFieldQuaternary={'lastcyberriskupdated'}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled') || task?.executionnotrequired === true ? true : false}
            ></LiveField>

            {
                task?.demandassessment?.vendor?.vendorname &&
                <DynamicVendorRating 
                    letter={task?.demandassessment?.vendor?.overallrating?.grade} 
                    score={task?.demandassessment?.vendor?.overallrating?.score}
                    lastmodifieddate={task?.demandassessment?.vendor?.lastcyberriskupdated}
                ></DynamicVendorRating>
            }

            {/* 2. Does this request align to the Business / Functions / Digital technology strategy? */}
            <LiveField
                type={'choice'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.businessfunctiontechalignment'}
                defaultValue={task?.demandassessment?.businessfunctiontechalignment}
                label={<label className='font-medium'> 2. Does this request align to the Business / Functions / Digital technology strategy? </label>}
                options={[
                    'Yes',
                    'No',
                    'Unknown',
                    'N/A',
                ]}
                styleInput={{ width: '350px' }}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/* 3. Please provide any relevant commentary regarding strategy alignment */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.strategyalignmentcommentary'}
                label={<label className='font-medium'> 3. Please provide any relevant commentary regarding strategy alignment</label>}
                defaultValue={task?.demandassessment?.strategyalignmentcommentary}
                placeholder={'Summarise strategy alignment'}
                styleInput={{ maxWidth: '625px' }}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/*  4. How will costs be allocated to the Business? */}
            <div className='Form-Row w-full'>
                <label className='font-medium'> 4. How will costs be allocated to the Business? </label>

                {/* Cost Allocation Table  */}
                <div className='border border-[#D8D8D8] rounded'>
                    <table className='table-fixed w-full'>
                        <thead>
                            <tr className='border-b-[#D8D8D8] border-solid border-b-2'>
                                <th className='py-[15px] px-[20px] font-medium text-sm'>Project Code</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Task Number</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Expenditure Type</th>
                                <th className='py-[15px] px-[20px] font-medium text-sm'>Allocation (%)</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Allocation ($)</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Business Unit</th>
                                <th className='py-[15px] px-[15px] font-medium text-sm min-w-20'>
                                    <Tooltip
                                        message='+ Add allocation'
                                        children={
                                            <img className='cursor-pointer' src={task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ? AddDisabled : Add} alt='add' onClick={() => task?.status === 'Completed' ? null : setAddAllocationItemPaneOpen(true)}></img>
                                        }
                                    ></Tooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                !task?.demandassessment?.allocations?.length ? 
                                (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className='text-center p-[2%]'>
                                                Click on the '+' icon to add a new item.
                                            </div>
                                        </td>
                                    </tr>
                                ) 
                                : 
                                (
                                    task?.demandassessment?.allocations.map((item, index) => (
                                        <tr key={index} className='border-b-[#C8C8C9] border-solid border-b last:border-0 hover:bg-[#F0F7F7]'>

                                            {/* Project Code */}
                                            <td className='py-[15px] px-[20px] text-sm'>
                                                {item.projectcode}
                                            </td>

                                            {/* Task Number */}
                                            <td className='py-[15px] px-[8px] text-sm'>
                                                {item.tasknumber}
                                            </td>

                                            {/* Expenditure Type */}
                                            <td className='py-[15px] px-[8px] text-sm'>
                                                {item.expendituretype}
                                            </td>

                                            {/* Allocation Percentage */}
                                            <td className='py-[15px] px-[20px] text-sm'>
                                                {item.allocationpercentage}%
                                            </td>

                                            {/* Allocation Amount */}
                                            <td className='py-[15px] px-[8px] text-sm'>
                                                ${item.allocationamount}
                                            </td>

                                            {/* Business Unit */}
                                            <td className='py-[15px] px-[8px] text-sm'>
                                                {item.businessunit}
                                            </td>

                                            {
                                                task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ?
                                                <td className='py-[15px] px-[15px]'>
                                                    
                                                </td>
                                                :
                                                <td className='py-[15px] px-[15px]'>
                                                    <KebabDropdown>
                                                        <div className='bg-white  rounded shadow-lg z-10'>

                                                            <button className='block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100' onClick={() => handleAllocationItemEditClick(index)}>
                                                                Edit
                                                            </button>

                                                            <button className='block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100' onClick={() => handleAllocationItemDeleteClick(index)}>
                                                                Remove
                                                            </button>

                                                        </div>
                                                    </KebabDropdown>
                                                </td>
                                            }
                                        </tr>
                                    ))
                                )
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 5. What is the Digital recommendation for this request? */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.digitalrecommendation'}
                label={<label className='font-medium'>5. What is the Digital recommendation for this request?</label>}
                defaultValue={task?.demandassessment?.digitalrecommendation}
                placeholder={'Based on engagement to date, does Digital endorse the scope of this request?'}
                styleInput={{ maxWidth: '625px' }}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/*  6. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.*/}
            <div className='mt-3'>
                <label className='font-medium'> 6. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.</label>

                {/* Attach Files */}
                <FileUploadLiveField
                    label={<label className='font-medium'> Attach documents </label>}
                    defaultValue={task?.demandassessment?.attacheddocuments}
                    collectionid={collectionid}
                    documentid={documentid}
                    fieldpath={'demandassessment.attacheddocuments'}
                    disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
                ></FileUploadLiveField>

                {/* Add links */}
                <div>
                    <label className='font-medium mb-2'> Add links </label>
                    <DynamicAddLinkLive
                        links={task?.demandassessment?.links}
                        collectionid={collectionid}
                        documentid={documentid}
                        fieldpath={'demandassessment.links'}
                        disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
                    ></DynamicAddLinkLive>
                </div>
            </div>

            {/* ===================================================== */}
            {/*  Add Allocation Item Pane                             */}
            {/* ===================================================== */}

            {
                addAllocationItemPaneOpen && 
                <AddAllocationItemPane
                    task={task}
                    addAllocationItemPaneOpen={addAllocationItemPaneOpen}
                    setAddAllocationItemPaneOpen={setAddAllocationItemPaneOpen}
                ></AddAllocationItemPane>
            }

            {/* ===================================================== */}
            {/*  Edit Allocation Item Pane                            */}
            {/* ===================================================== */}

            {
                editAllocationItemPaneOpen && 
                <EditAllocationItemPane
                    task={task}
                    editAllocationItemPaneOpen={editAllocationItemPaneOpen}
                    setEditAllocationItemPaneOpen={setEditAllocationItemPaneOpen}
                    selectedAllocationIndex={selectedAllocationIndex}
                ></EditAllocationItemPane>
            }

        </div>
    )
}
